import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { PORTAL_CATALOG, checkedPortal, defaultPortal, portalLinks } from '../common/portal-settings.mjs'
import { NAV_MENUS, QUICK_MENUS } from '@/portal/navigation.mjs'

// 정본 메뉴(대메뉴·하위 메뉴)에 속한 경로. 홈 바로가기처럼 메뉴 밖에서 메뉴 경로로 연결할 때 숨김 설정을 따르기 위해 쓴다.
const menuPaths = new Set(NAV_MENUS.flatMap(menu => [menu.to, ...menu.children.map(row => row.to)]))
const childKeys = new Map(PORTAL_CATALOG.filter(row => row.kind === 'CHILD').map(row => [`${row.parent}\n${row.to}`, row.key]))

/** 공개 포털 설정만 메모리에 보관한다. 기업 브랜딩/인증 토큰과 공유하지 않는다. */
export const usePortalStore = defineStore('portal-settings', () => {
  const current = ref(defaultPortal()), unavailable = ref(false)
  let pending, loadedAt = 0
  async function load(force = false) {
    if (pending) return pending
    if (!force && Date.now() - loadedAt < 60000) return
    pending = (async () => {
      try { const response = await fetch('/api/public/portal/settings', { credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(5000) }); if (!response.ok) throw new Error('PORTAL_UNAVAILABLE'); const body = await response.json(); current.value = checkedPortal(body.data); unavailable.value = false }
      catch { current.value = defaultPortal(); unavailable.value = true }
      finally { loadedAt = Date.now(); pending = null }
    })()
    return pending
  }
  const values = computed(() => new Map(current.value.items.map(row => [row.key, row])))
  // 포털 메뉴 관리의 표시·순서·이름을 반영한다. 메뉴 키는 저장된 설정과 맞물린 고정값이라 위치가 아니라 정본 카탈로그의 경로로 찾는다.
  const menus = computed(() => NAV_MENUS.map((menu, index) => ({ menu, item: values.value.get(`menu-${index}`), index })).filter(row => row.item?.visible).sort((a, b) => a.item.order - b.item.order)
    .map(({ menu, item, index }) => {
      const children = menu.children.map(row => ({ row, item: values.value.get(childKeys.get(`menu-${index}\n${row.to}`)) })).filter(row => row.item?.visible).sort((a, b) => a.item.order - b.item.order)
        .map(({ row, item: child }) => ({ ...row, title: child.title || row.title }))
      // 대메뉴를 누르면 첫 번째로 보이는 하위 메뉴로 이동한다. to는 하위 메뉴 묶음·활성 판정용으로 그대로 둔다.
      return { ...menu, title: item.title || menu.title, children, href: children[0]?.to || menu.to }
    }))
  /** 라우트의 정본 페이지 정보(navigation.mjs)에 포털 메뉴 관리에서 바꾼 이름을 입힌다. 화면 제목·위치 표시가 메뉴 이름과 같게 한다. */
  function pageOf(page) {
    if (!page) return page
    const index = NAV_MENUS.findIndex(menu => menu.to === page.parent); if (index < 0) return page
    const menu = values.value.get(`menu-${index}`), child = values.value.get(childKeys.get(`menu-${index}\n${page.path}`))
    return { ...page, group: menu?.title || page.group, title: child ? child.title || page.title : page.path === page.parent ? menu?.title || page.title : page.title }
  }
  const visiblePaths = computed(() => new Set(menus.value.flatMap(menu => [menu.to, ...menu.children.map(row => row.to)])))
  /** 메뉴 경로면 포털 설정에서 보이는 경우에만 true, 메뉴 밖 경로는 항상 true. */
  const visiblePath = path => !menuPaths.has(path) || visiblePaths.value.has(path)
  const quick = computed(() => portalLinks(current.value, 'QUICK').map(row => ({ ...QUICK_MENUS.find(item => `quick-${item.key}` === row.key), ...(row.to ? { to: row.to } : {}), ...(row.custom ? { title: row.title } : {}) })))
  const footerLinks = computed(() => portalLinks(current.value, 'FOOTER'))
  const footer = computed(() => current.value.footer)
  return { current, unavailable, menus, pageOf, visiblePath, quick, footerLinks, footer, load }
})
